"use client"

import { cn } from "@/lib/utils"
import { useEffect, useState } from "react"
import { Check, X } from "lucide-react"

interface AnimatedResultProps {
  type: "success" | "error"
  title: string
  description?: string
  className?: string
}

export function AnimatedResult({ type, title, description, className }: AnimatedResultProps) {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className={cn("flex flex-col items-center justify-center text-center", className)}>
      <div
        className={cn(
          "relative flex items-center justify-center w-24 h-24 rounded-full transition-all duration-500",
          show ? "scale-100 opacity-100" : "scale-50 opacity-0",
          type === "success" ? "bg-green-500/10" : "bg-red-500/10"
        )}
      >
        {/* Animated ring */}
        <svg
          className={cn(
            "absolute inset-0 w-full h-full",
            show ? "animate-[spin_1s_ease-out]" : ""
          )}
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={type === "success" ? "#22c55e" : "#ef4444"}
            strokeWidth="2"
            strokeDasharray="283"
            strokeDashoffset={show ? "0" : "283"}
            className="transition-all duration-700 ease-out"
            style={{ transformOrigin: "center", transform: "rotate(-90deg)" }}
          />
        </svg>
        
        {/* Icon */}
        <div
          className={cn(
            "transition-all duration-500 delay-300",
            show ? "scale-100 opacity-100" : "scale-0 opacity-0"
          )}
        >
          {type === "success" ? (
            <Check className="w-12 h-12 text-green-500" strokeWidth={3} />
          ) : (
            <X className="w-12 h-12 text-red-500" strokeWidth={3} />
          )}
        </div>
        
        {/* Glow effect */}
        <div
          className={cn(
            "absolute inset-0 rounded-full blur-xl transition-opacity duration-700",
            show ? "opacity-100" : "opacity-0",
            type === "success" ? "bg-green-500/20" : "bg-red-500/20"
          )}
        />
      </div>
      
      <h3
        className={cn(
          "mt-6 text-2xl font-semibold transition-all duration-500 delay-500",
          show ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
          type === "success" ? "text-green-500" : "text-red-500"
        )}
      >
        {title}
      </h3>
      
      {description && (
        <p
          className={cn(
            "mt-2 text-muted-foreground transition-all duration-500 delay-700",
            show ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          )}
        >
          {description}
        </p>
      )}
    </div>
  )
}
